import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import ChatContainer from "../../../../../components/chat/chatContainer/ChatContainer";
import MessageComposer from "../../../../../components/chat/messageComposer/MessageComposer";
import Loader from "../../../../../components/loader/Loader";
import PrimeBadge from "../../../../../components/primeBadge/PrimeBadge";
import UserAvatar from "../../../../../components/userAvatar/UserAvatar";
import {
  markAsReadByAdmin,
  queueSupportMessage,
  toggleChatAccess,
} from "../../../../../features/supportChat/supportChatSlice";
import PendingPrizesBadge from "./PendingPrizesBadge";
import styles from "./chatWindow.module.css";

// Who sees the support chat in the app (its ChatScreen gate): Prime (prime2)
// players always, anyone else while their chat access is on. A Prime grant
// without a tier counts as Prime (the backend's planOf).
const isPrimeTwo = (player) =>
  Boolean(player?.isPrime) && (player?.primeTier || "prime2") === "prime2";

const ChatWindow = () => {
  const dispatch = useDispatch();
  const activeChatId = useSelector((state) => state.supportChat.activeChatId);
  // The list's copy is the freshest (access changes, plan changes on refresh).
  const listedPlayer = useSelector(
    (state) =>
      state.supportChat.chats.find((chat) => chat._id === state.supportChat.activeChatId)
        ?.userId,
  );
  const openedPlayer = useSelector((state) => state.supportChat.activeUser);
  const player = listedPlayer || openedPlayer;
  const pendingAccessUserId = useSelector(
    (state) => state.supportChat.chatAccessPendingUserId,
  );
  const { rolePermissions } = useSelector((state) => state.user);
  const canReply = rolePermissions.includes("reply-user-chats");
  const canToggleAccess = rolePermissions.includes("control-chat-access");

  const playerId = player?._id;

  useEffect(() => {
    dispatch(markAsReadByAdmin(activeChatId));
  }, [activeChatId, dispatch]);

  const hasAccess = Boolean(player?.chatAccess);
  const alwaysOpen = isPrimeTwo(player);
  const accessPending = Boolean(playerId) && pendingAccessUserId === playerId;

  const handleAccessToggle = () => {
    if (!playerId || accessPending) return;
    dispatch(toggleChatAccess({ userId: playerId, access: !hasAccess }));
  };

  let accessState = { tone: styles.statusOff, text: "لا يرى المحادثة في التطبيق" };
  if (hasAccess) accessState = { tone: styles.statusOn, text: "الدعم المباشر مفعّل" };
  else if (alwaysOpen) {
    accessState = { tone: styles.statusPrime, text: "يرى المحادثة دائماً كمشترك برايم" };
  }

  return (
    <div className={styles.window}>
      <header className={styles.header}>
        <div className={styles.identity}>
          <span className={styles.avatar}>
            <UserAvatar src={player?.userImage?.path} alt="" />
          </span>
          <div className={styles.identityText}>
            <div className={styles.nameRow}>
              <h2 className={styles.name} dir="auto">
                {player?.name || "لاعب"}
              </h2>
              <PrimeBadge
                isPrime={player?.isPrime}
                primeTier={player?.primeTier}
                size="medium"
              />
              {playerId && <PendingPrizesBadge key={playerId} playerId={playerId} />}
            </div>
            <p className={`${styles.status} ${accessState.tone}`}>
              <span className={styles.statusDot} aria-hidden="true" />
              {accessState.text}
            </p>
          </div>
        </div>

        {canToggleAccess && (
          <label className={styles.accessToggle}>
            <span>الدعم المباشر</span>
            <button
              type="button"
              role="switch"
              aria-checked={hasAccess}
              aria-busy={accessPending}
              disabled={accessPending || !playerId}
              className={`${styles.switch} ${hasAccess ? styles.switchOn : ""}`}
              onClick={handleAccessToggle}
            >
              <span className={styles.switchThumb}>
                {accessPending && <Loader size={10} />}
              </span>
            </button>
          </label>
        )}
      </header>

      <div className={styles.thread}>
        <ChatContainer key={activeChatId} chatId={activeChatId} />
      </div>

      {canReply ? (
        <MessageComposer
          key={activeChatId}
          draftKey={`chat:${activeChatId}`}
          onSend={({ text }) => dispatch(queueSupportMessage({ chatId: activeChatId, text }))}
          notice={
            hasAccess || alwaysOpen
              ? ""
              : "الدعم المباشر غير مفعّل لهذا اللاعب: يصله إشعار بردّك، لكنه لا يستطيع فتح المحادثة في التطبيق."
          }
        />
      ) : (
        <div className={styles.readOnly}>
          يمكنك قراءة هذه المحادثة فقط؛ الرد يحتاج صلاحية الرد على المحادثات.
        </div>
      )}
    </div>
  );
};

export default ChatWindow;
